import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type Column, type ColumnDef, type ColumnHelper, type OnChangeFn, type PaginationState, type RowSelectionState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { IndeterminateCheckbox } from "./IndeterminateCheckbox";
import { Paginator } from "./Paginator";

export function TypedTable<T>(props: TypedTableOptions<T>) {
	const filteredCols = useMemo(() => props.columns.filter((col) => (col.meta as Record<string, unknown> | undefined)?.hidden !== true), [props.columns]); // Filter hidden

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: 10, //default page size
	});
	const [search, setSearch] = useState("");

	const table = useReactTable({
		columns: filteredCols,
		data: props.data,
		getRowId: props.getRowId,

		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: props.pagination == true ? getPaginationRowModel() : undefined,
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: props.globalSearch == true ? getFilteredRowModel() : undefined,
		globalFilterFn: "includesString",

		onPaginationChange: setPagination, //update the pagination state when internal APIs mutate the pagination state
		onGlobalFilterChange: setSearch, //update the global filter state when internal APIs mutate the global filter state
		onRowSelectionChange: props.setSelection, //update the row selection state when internal APIs mutate the row selection state

		state: {
			pagination: pagination,
			globalFilter: search,
			rowSelection: props.selection,
		},
	});

	const columnCount = table.getHeaderGroups().reduce((acc, group) => acc + group.headers.reduce((acc, col) => acc + col.colSpan, 0), 0);


	return (
		<div>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				{props.pagination ? (
					<label>
						<span>Mostrar</span>
						<select style={{ margin: "0 5px" }} value={pagination.pageSize} onChange={e => setPagination(old => ({ ...old, pageSize: Number(e.target.value) }))}>
							<option value="10">10</option>
							<option value="25">25</option>
							<option value="50">50</option>
							<option value="100">100</option>
						</select>
						<span>artículos</span>
					</label>
				) : null}
				{!props.pagination && (
					// Empty div to align search box to the right
					<div />
				)}

				{props.globalSearch ? (
					<label>
						<span>Buscar: </span>
						<input type="search" style={{ margin: "0 5px", border: "1px solid #ccc", borderRadius: "4px" }} value={search} onChange={e => setSearch(e.target.value)} />
					</label>
				) : null}
			</div>

			<table className={props.className} style={{}}>
				<thead>
					{table.getHeaderGroups().map(headerGroup => {
						return (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => (
									<th key={header.id} colSpan={header.colSpan} className={`${getMetaClass(header.column)} ${getSortClass(header.column)}`} style={getMetaStyle(header.column)} onClick={header.column.getToggleSortingHandler()} >
										{flexRender(header.column.columnDef.header, header.getContext())}
									</th>
								))}
							</tr>
						);
					})}
				</thead>
				<tbody>
					{table.getRowModel().rows.map(row => (
						<tr key={row.id} className={props.rowClassName ? props.rowClassName(row.original) : ""} onClick={e => props.onRowClick?.(e, row.original)}>
							{row.getVisibleCells().map(cell => (
								<td key={cell.id} className={getMetaClass(cell.column)} style={getMetaStyle(cell.column)} >
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</td>
							))}
						</tr>
					))}
					{props.data.length === 0 && (
						<tr>
							<td colSpan={columnCount} className="dt-center">
								{props.noDataMessage ?? "No hay datos disponibles"}
							</td>
						</tr>
					)}
				</tbody>
			</table>
			{props.pagination != undefined && (
				<BottomTablePagination
					pagination={pagination}
					setPagination={setPagination}
					canNextPage={table.getCanNextPage()}
					canPreviousPage={table.getCanPreviousPage()}
					pageCount={table.getPageCount()}
					pageIndex={table.getState().pagination.pageIndex}
					itemCount={table.getFilteredRowModel().rows.length}
				/>
			)}
		</div>
	);
}

interface TypedTableOptions<T> {
	readonly data: T[],
	readonly getRowId?: (originalRow: T) => string,
	readonly columns: Array<ColumnDef<T, any>>,

	readonly selection?: RowSelectionState;
	readonly setSelection?: OnChangeFn<RowSelectionState>,

	readonly pagination?: true;
	readonly globalSearch?: true;

	readonly className?: string,
	// scrollY?: string,
	readonly rowClassName?: (data: T) => string,
	readonly onRowClick?: (e: React.MouseEvent, data: T) => void,

	readonly noDataMessage?: string;
}

export function getSelectionCell<T>(columnHelper: ColumnHelper<T>, options?: Partial<ColumnDef<T, any>>): ColumnDef<T, any> {
	return columnHelper.display({
		id: "checkbox",
		// header: () => <input type="checkbox" className="checkboxRow" style={{ height: 20 }} />,
		cell: ({ row }) => (
			<IndeterminateCheckbox
				className="checkboxRow"
				style={{ height: 20 }}
				checked={row.getIsSelected()}
				disabled={!row.getCanSelect()}
				// indeterminate={row.getIsSomeSelected()}
				onChange={(e) => {
					row.getToggleSelectedHandler()(e);
				}}
			/>
		),
		...options,
	});
}
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
function getSortClass<T>(col: Column<T, any>): string {
	if (col.getCanSort() === false) return "";
	const direction = col.getIsSorted();
	if (direction == false) return "sorting";
	return direction == "asc" ? "sorting sorting_asc" : "sorting sorting_desc";
};
function getMetaClass<T>(col: Column<T, any>): string {
	return (col.columnDef.meta as Record<string, any> | null)?.className ?? "";
};
function getMetaStyle<T>(col: Column<T, any>): Partial<React.CSSProperties> {
	return (col.columnDef.meta as Record<string, any> | null)?.style ?? {};
};
/* eslint-enable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/no-unsafe-return */

interface BottomTablePaginationProps {
	readonly pagination: PaginationState;
	readonly setPagination: OnChangeFn<PaginationState>;
	readonly canNextPage: boolean;
	readonly canPreviousPage: boolean;
	readonly pageCount: number;
	readonly pageIndex: number;
	readonly itemCount: number;
}
export function BottomTablePagination(props: BottomTablePaginationProps) {
	return (
		<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
			<div>
				Mostrando {props.pagination.pageIndex * props.pagination.pageSize + 1} a {Math.min((props.pagination.pageIndex + 1) * props.pagination.pageSize, props.itemCount)} de {props.itemCount} artículos
			</div>

			<Paginator
				pageCount={props.pageCount}
				pageIndex={props.pageIndex}
				shownPages={5}
				onPageChange={newPageIndex => props.setPagination(old => ({ ...old, pageIndex: newPageIndex }))}
				canPrevious={props.canPreviousPage}
				canNext={props.canNextPage}
			/>
		</div>
	);
}